import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import {
  CATEGORIES,
  STATUSES,
  useAllFeedback,
  useUpdateFeedback,
  type Category,
  type ManagedFeedback,
  type Status,
} from "@/lib/feedback";
import { cn } from "@/lib/cn";

const STATUS_VARIANT: Record<Status, "neutral" | "primary" | "success" | "destructive"> =
  {
    New: "neutral",
    "In Progress": "primary",
    Resolved: "success",
    Declined: "destructive",
  };

function Item({ item }: { item: ManagedFeedback }) {
  const update = useUpdateFeedback();
  const [remark, setRemark] = useState(item.adminRemark ?? "");
  const dirty = remark !== (item.adminRemark ?? "");

  return (
    <li className="border-border bg-card rounded-lg border p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Badge variant="outline">{item.category}</Badge>
        <Badge variant={STATUS_VARIANT[item.status]}>{item.status}</Badge>
        <span className="text-muted-foreground text-sm">
          {item.raiserName ?? item.raiserEmail}
        </span>
        <span className="text-muted-foreground ml-auto text-xs">
          {new Date(item.createdAt).toLocaleString()}
        </span>
      </div>

      <p className="text-sm whitespace-pre-wrap">{item.body}</p>
      {item.page && (
        <p className="text-muted-foreground mt-1 text-xs break-all">
          Sent from <code>{item.page}</code>
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-start gap-2">
        <Select
          value={item.status}
          disabled={update.isPending}
          aria-label="Status"
          onChange={(e) =>
            update.mutate({ id: item.id, status: e.target.value as Status })
          }
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>

        <textarea
          rows={2}
          maxLength={4000}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Reply to the person who raised this…"
          className="border-input bg-background text-foreground placeholder:text-muted-foreground min-w-56 flex-1 rounded-md border px-3 py-2 text-sm"
        />

        <Button
          size="sm"
          variant="outline"
          disabled={!dirty || update.isPending}
          onClick={() => update.mutate({ id: item.id, adminRemark: remark || null })}
        >
          Save reply
        </Button>
      </div>

      <p className="text-muted-foreground mt-2 text-xs">
        Any change here notifies {item.raiserName ?? item.raiserEmail} in the app.
      </p>
    </li>
  );
}

const FILTERS = ["All", ...STATUSES] as const;

export function FeedbackAdmin() {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("New");
  const [category, setCategory] = useState<Category | "All">("All");
  const { data, isPending } = useAllFeedback(filter, category);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-1">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              filter === f
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {f}
          </button>
        ))}
        <Select
          value={category}
          aria-label="Category"
          onChange={(e) => setCategory(e.target.value as Category | "All")}
          className="ml-auto"
        >
          <option value="All">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </div>

      {isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : data && data.length > 0 ? (
        <ul className="space-y-3">
          {data.map((item) => (
            <Item key={item.id} item={item} />
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground py-8 text-center text-sm">
          Nothing {filter === "All" ? "yet" : `with status “${filter}”`}
          {category === "All" ? "" : ` in ${category}`}.
        </p>
      )}
    </>
  );
}
